import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col } from "antd";
import { Content } from "@worcket/common/components/layout";
import { Formik } from "formik";
import * as R from "ramda";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { validators as chatbotValidators } from "../../../../../components/Chatbot";
import { isLoading } from "../../../../../utils/actions";
import {
  saveOpportunityQuestions,
  uploadOpportunityDocument,
  SAVE_OPPORTUNITY_QUESTIONS,
  UPLOAD_OPPORTUNITY_DOCUMENT,
  GO_TO_PREVIOUS_WIZARD_STEP,
  GO_TO_NEXT_WIZARD_STEP
} from "../../../../../store/actions/opportunity";
import uploadChatbotDocuments from "../../../../../components/Chatbot/UploadChatbotDocuments";
import { INITIAL_CHATBOT_VALIDATION_PATH } from "../../../../../components/Chatbot/validators";
import * as gtag from "../../../../../lib/gtag";
import Button from "../../../../../components/Button";

import {
  FIXED_QUESTIONS_KEYS,
  SCREENING_FIXED_QUESTIONS,
  isFixedQuestion,
  FIXED_OPTIONS_KEYS,
  SCREENING_FIXED_OPTIONS
} from "./ScreeningTable/constants";
import FormWrapper from "./FormWrapper";

const Disabled = styled.div`
  position: absolute;
  background: #f7f9fa;
  height: 100%;
  width: 100%;
  z-index: 1;
  opacity: 0.6;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

function ScreeningForm({
  opportunity,
  isDisabled,
  savingQuestions,
  newOpportunity,
  goToNextStep,
  goToPreviousStep,
  saveOpportunityQuestions,
  uploadOpportunityDocument,
  fixedOptionsEnabled = FIXED_OPTIONS_KEYS,
  fixedQuestionsEnabled = FIXED_QUESTIONS_KEYS,
  section,
  setIsEditingScreeening,
  isEditingScreening
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation(["opportunity", "global"]);
  const [isScreeningDisabled, setDisabled] = useState(true);
  const sectionName = useMemo(() => t("opportunity:screening.sectionName"), [
    t
  ]);
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);

  useEffect(() => {
    setIsEditingScreeening && setIsEditingScreeening(isScreeningDisabled);
  }, [isScreeningDisabled, setIsEditingScreeening]);

  const fixedOptionsInitialValues = useMemo(
    () =>
      R.mergeAll(
        fixedOptionsEnabled.map(key => ({
          [key]:
            opportunity[key] !== undefined
              ? opportunity[key]
              : SCREENING_FIXED_OPTIONS[key]
        }))
      ),
    [fixedOptionsEnabled, opportunity]
  );

  const opportunityQuestions = useMemo(
    () => R.prop("questionsAdvanced", opportunity),
    [opportunity]
  );

  const fixedQuestionsInitialValues = React.useMemo(() => {
    /*
      All this logic is here for UI purposes

      To set the context: Our backend can only receive questions that have the following structure:
      questionsAdvanced: Joi.array().optional().items(Joi.object().keys({
        id: Joi.string().optional(),
        enabled: Joi.boolean().optional(),
        text: Joi.string().required(),
        type: Joi.string().required().valid('video', 'number', 'multiple', 'referral'),
        knockout: Joi.boolean(),
        validRange: Joi.array().items(Joi.number(), Joi.number()),
        options: Joi.array().items(Joi.alternatives().try(Joi.string(), Joi.number(), Joi.object())),
        validOptions: Joi.array().items(Joi.alternatives().try(Joi.string(), Joi.number(), Joi.object()))
      }))
    */

    // Get the fixed questions that are already set in the opp (there won't be any questions set on creation).
    const fixedQuestions = R.filter(
      R.compose(
        R.find(R.__, fixedQuestionsEnabled),
        R.equals(R.__),
        R.prop("type")
      )
    )(opportunityQuestions);

    // Get all the default values (there can be questions that are enabled by default and questions that are not).
    // This also contains all the types that fixed questions that we have
    const defaultQuestions = R.map(
      type => ({
        ...SCREENING_FIXED_QUESTIONS[type],
        type,
        text: t(`opportunity:screening.${type}`)
      }),
      fixedQuestionsEnabled
    );

    if (R.isEmpty(fixedQuestions)) {
      // If there's no questions set and the opp is new, just go with the default values.
      if (newOpportunity) return defaultQuestions;
      // If there's no questions and the opp isn't new, set all the values to false (fallback for all opportunities created before this feature).
      else return R.map(R.assoc("enabled", false), defaultQuestions);
    } else {
      // Get all the types of questions that are already set
      const fixedQuestionTypes = R.map(R.prop("type"), fixedQuestions);

      // If there's questions set, grab all the defaults and set the questions to their respective enabled states:
      return R.map(
        R.when(
          R.compose(
            R.includes(R.__, fixedQuestionTypes),
            R.prop("type")
          ),
          question => {
            const settedQuestion = R.find(
              R.propEq("type", question.type),
              fixedQuestions
            );
            return {
              ...question,
              ...settedQuestion,
              error:
                question.isEmpty && question.isEmpty(settedQuestion)
                  ? false
                  : // TODO move to custom error logic
                    (question.isValid && !question.isValid(settedQuestion)) ||
                    (question.type === "chatbot" &&
                      chatbotValidators.getErrorText(
                        R.path(INITIAL_CHATBOT_VALIDATION_PATH, settedQuestion)
                      )),
              text: question.text,
              configurable: question.configurable
            };
          }
        ),
        defaultQuestions
      );
    }
  }, [fixedQuestionsEnabled, newOpportunity, opportunityQuestions, t]);

  // Get all the questions that are not fixed questions
  const questionsInitialValues = useMemo(() => {
    return R.reject(
      question => R.includes(R.prop("type", question), FIXED_QUESTIONS_KEYS),
      opportunityQuestions
    );
  }, [opportunityQuestions]);

  const initialValues = useMemo(() => {
    return {
      advance: false,
      fixedOptions: { ...fixedOptionsInitialValues },
      questions: R.concat(fixedQuestionsInitialValues, questionsInitialValues)
    };
  }, [
    fixedOptionsInitialValues,
    fixedQuestionsInitialValues,
    questionsInitialValues
  ]);

  return (
    <div ref={ref}>
      <Content>
        <h2>{t("opportunity:screening.title")}</h2>
        <p style={{ marginBottom: "40px" }}>
          {t("opportunity:screening.subtitle1")}
          <br />
          {t("opportunity:screening.subtitle2")}
        </p>
        <Row gutter={16}>
          <Col xs={24} md={24}>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              onSubmit={async values => {
                gtag.event({
                  category: "VacancyDetail",
                  action: "VacancyDetail.Screening-Save",
                  label: opportunity.id
                });

                await uploadChatbotDocuments(
                  opportunity.id,
                  R.find(R.propEq("type", "chatbot"), values.questions),
                  dispatch
                );

                await saveOpportunityQuestions({
                  id: opportunity.id,
                  formData: {
                    questionsAdvanced: R.map(
                      R.compose(
                        R.reject(R.isNil),
                        R.pick([
                          "id",
                          "enabled",
                          "text",
                          "type",
                          "config",
                          "knockout",
                          "validRange",
                          "options",
                          "validOptions"
                        ]),
                        question =>
                          isFixedQuestion(question)
                            ? { ...question, text: question.type }
                            : question
                      ),
                      values.questions
                    ),
                    ...values.fixedOptions
                  }
                });
                if (values.advance) goToNextStep();
                else goToPreviousStep();
              }}
            >
              {({ handleSubmit, values, setFieldValue, resetForm }) => (
                <>
                  <div style={{ position: "relative" }}>
                    {isScreeningDisabled && !newOpportunity && <Disabled />}
                    <FormWrapper
                      opportunity={opportunity}
                      isDisabled={isDisabled}
                      isScreeningDisabled={isScreeningDisabled}
                      loading={savingQuestions}
                      newOpportunity={newOpportunity}
                      goToNextStep={goToNextStep}
                      goToPreviousStep={goToPreviousStep}
                      handleSubmit={handleSubmit}
                      setFieldValue={setFieldValue}
                      uploadOpportunityDocument={uploadOpportunityDocument}
                      t={t}
                      values={values}
                      setDisabled={setDisabled}
                      initialValues={initialValues}
                      resetForm={() => {
                        resetForm(initialValues);
                      }}
                      isEditingScreening={isEditingScreening}
                    />
                  </div>
                  {isScreeningDisabled && !newOpportunity && (
                    <WrapperButton>
                      <Button
                        data-test="edit-screening-button"
                        disabled={isDisabled}
                        onClick={() => setDisabled(!isScreeningDisabled)}
                      >
                        {t("opportunity:screening.editQuestion")}
                      </Button>
                    </WrapperButton>
                  )}
                </>
              )}
            </Formik>
          </Col>
        </Row>
      </Content>
    </div>
  );
}

export default connect(
  state => ({
    newOpportunity: R.path(["opportunity", "newOpportunity"], state),
    savingQuestions:
      isLoading(UPLOAD_OPPORTUNITY_DOCUMENT, state) ||
      isLoading(SAVE_OPPORTUNITY_QUESTIONS, state)
  }),
  {
    saveOpportunityQuestions,
    uploadOpportunityDocument,
    goToNextStep: GO_TO_NEXT_WIZARD_STEP.run,
    goToPreviousStep: GO_TO_PREVIOUS_WIZARD_STEP.run
  }
)(ScreeningForm);
